// Payment Tracking convergence — the tab's five-second summary: the most
// important number (days delinquent) leads, then exceptions / next due /
// balance / YTD. Severity uses status tokens only (gold is brand).

import type { ReactNode } from "react";
import { Card, StatusBadge } from "@/design-system/components/ui";
import { formatCalendarDate } from "@/utils/datetime";
import type { ServicingLedgerSummaryDto } from "@/api/portfolioAdmin/servicingApi";
import { fmtMoney } from "./servicingFormat";

export function ServicingSummaryStrip({
  summary,
}: {
  summary: ServicingLedgerSummaryDto;
}) {
  const delinquencyTone =
    summary.daysDelinquent >= 30
      ? "danger"
      : summary.daysDelinquent > 0
        ? "warn"
        : "ok";

  return (
    <Card data-testid="servicing-summary-strip">
      <div className="flex flex-wrap items-start gap-x-space-7 gap-y-space-3">
        <Stat
          label="Days delinquent"
          testId="summary-days-delinquent"
          value={
            <span className="flex items-center gap-space-2">
              <span className="font-display font-bold text-h1 leading-none tabular-nums">
                {summary.daysDelinquent}
              </span>
              <StatusBadge tone={delinquencyTone}>
                {summary.daysDelinquent === 0 ? "Current" : "Delinquent"}
              </StatusBadge>
            </span>
          }
        />
        <Stat
          label="Open exceptions"
          testId="summary-open-exceptions"
          value={
            <span className="font-display font-bold text-h2 leading-none tabular-nums">
              {summary.openExceptions}
            </span>
          }
        />
        <Stat
          label="Next payment due"
          value={
            <span className="text-body font-semibold">
              {summary.nextDueDate ? formatCalendarDate(summary.nextDueDate) : "—"}
            </span>
          }
        />
        <Stat
          label="Scheduled payment"
          value={
            <span className="text-body font-semibold tabular-nums">
              {fmtMoney(summary.scheduledMonthlyPayment)}
            </span>
          }
        />
        {/* BLA Core §2.4 — shown only on a loan carrying the fee, where the
            scheduled payment above is no longer what the borrower is debited.
            On every other loan the strip renders exactly as it did. */}
        {(summary.paymentAdministrationFee ?? 0) > 0 && (
          <Stat
            label="Total debit"
            value={
              <span className="text-body font-semibold tabular-nums">
                {fmtMoney(summary.totalMonthlyDebit ?? summary.scheduledMonthlyPayment)}
              </span>
            }
          />
        )}
        <Stat
          label="Current balance"
          value={
            <span className="text-body font-semibold tabular-nums">
              {fmtMoney(summary.currentBalance)}
            </span>
          }
        />
        <Stat
          label="Received YTD"
          value={
            <span className="text-body font-semibold tabular-nums">
              {fmtMoney(summary.receivedYearToDate)}
            </span>
          }
        />
      </div>
    </Card>
  );
}

function Stat({
  label,
  value,
  testId,
}: {
  label: string;
  value: ReactNode;
  testId?: string;
}) {
  return (
    <div className="flex flex-col gap-space-1 min-w-[8rem]" data-testid={testId}>
      <span className="text-caption text-gray-mid uppercase tracking-wide">
        {label}
      </span>
      {value}
    </div>
  );
}
